import { useState } from 'react';
import { ORPCError } from '@orpc/client';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import {
  MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE,
  type VendorPortalArchiveFileRange,
  type VendorPortalFile,
} from '@erp/rpc';
import { rpcClient } from '@/api/rpc/client';
import { startRpcDownloadInFrame } from '@/api/rpc/downloadUrl';
import {
  type VendorPortalFileFilters,
  useDeleteVendorPortalFiles,
} from './useVendorPortalFiles';
import { reportDeleteResult } from './reportDeleteResult';

const ARCHIVE_PATH = ['pricing', 'vendorPortalFiles', 'downloadArchive'];

/** A zip the server accepted and the browser is saving, for the page to list. */
export interface StartedVendorPortalDownload {
  id: number;
  fileCount: number;
  /** Files storage no longer had; the zip lists them in MISSING-FILES.txt. */
  missingFileCount: number;
  /** The list rows a page's zip holds; absent for a selection or every match. */
  fileRange?: VendorPortalArchiveFileRange;
}

/**
 * Selection and file actions for one page of Vendor Portal files: download one, download
 * the selection, the page or everything the filters match as one zip, or permanently delete the
 * selection. The selection holds ids on the current page and is dropped whenever `scope`
 * (the page and filters) changes, however it changed: a click, Back or Forward.
 */
export function useVendorPortalFileActions(
  files: VendorPortalFile[],
  scope: string,
) {
  const gt = useGT();
  const [selection, setSelection] = useState<{
    scope: string;
    ids: ReadonlySet<string>;
  }>(() => ({ scope, ids: new Set() }));
  if (selection.scope !== scope) setSelection({ scope, ids: new Set() });
  const selectedIds =
    selection.scope === scope ? selection.ids : new Set<string>();
  function setSelectedIds(ids: ReadonlySet<string>) {
    setSelection({ scope, ids });
  }
  const [startedDownloads, setStartedDownloads] = useState<
    StartedVendorPortalDownload[]
  >([]);
  const deleteFiles = useDeleteVendorPortalFiles();
  const selectedFiles = files.filter((file) =>
    selectedIds.has(file.documentId),
  );

  function toggle(documentId: string, checked: boolean) {
    const next = new Set(selectedIds);
    if (checked) next.add(documentId);
    else next.delete(documentId);
    setSelectedIds(next);
  }

  function toggleAll(checked: boolean) {
    setSelectedIds(
      new Set(checked ? files.map((file) => file.documentId) : []),
    );
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function downloadFile(
    file: Pick<VendorPortalFile, 'documentId' | 'fileName'>,
  ) {
    try {
      const { signedUrl, fileName } = await rpcClient.core.documents.getPreview(
        { documentId: file.documentId },
      );
      await saveFile(signedUrl, fileName);
    } catch {
      toast.error(
        gt('Could not download {fileName}', {
          context: 'Toast shown when downloading a vendor portal file fails',
          fileName: file.fileName,
        }),
      );
    }
  }

  /**
   * One zip, streamed by the server and saved by the browser's own download manager, so
   * the files never pass through this tab's memory. The hidden frame that starts it cannot
   * report a refusal, so the server checks the selection first and a refusal (too many
   * files, nothing left in storage) is shown here instead of a zip that never arrives.
   * The zip then takes exactly the files the check accepted.
   *
   * Nothing waits on it: the user can page on and start the next zip at once. A started
   * zip is listed on the page, not toasted, because a toast sits over the page controls.
   */
  async function downloadArchive(
    input: VendorPortalFileFilters & {
      documentIds?: string[];
      fileRange?: VendorPortalArchiveFileRange;
    },
  ) {
    try {
      const { documentIds, missingFileCount } =
        await rpcClient.pricing.vendorPortalFiles.checkArchive(input);
      await startRpcDownloadInFrame(ARCHIVE_PATH, {
        documentIds,
        ...(input.fileRange ? { fileRange: input.fileRange } : {}),
      });
      setStartedDownloads((current) => [
        ...current,
        {
          id: current.length + 1,
          fileCount: documentIds.length,
          missingFileCount,
          fileRange: input.fileRange,
        },
      ]);
    } catch (error) {
      toast.error(archiveRefusalMessage(error, gt));
    }
  }

  /**
   * The files on the page when the user clicked, whatever page they have moved on to by
   * the time the server answers.
   */
  async function downloadPage(offset: number) {
    await downloadArchive({
      documentIds: files.map((file) => file.documentId),
      fileRange: { first: offset + 1, last: offset + files.length },
    });
  }

  async function downloadSelected() {
    await downloadArchive({
      documentIds: selectedFiles.map((file) => file.documentId),
    });
  }

  /** Delete the selection; files that were kept stay selected so a retry is one click. */
  async function deleteSelected() {
    try {
      const result = await deleteFiles.mutateAsync({
        documentIds: selectedFiles.map((file) => file.documentId),
      });
      reportDeleteResult(result, gt);
      // Only files still selected: the user may have cleared the selection or moved on
      // while the request ran.
      const kept = new Set(
        [...result.inUse, ...result.failed].map((entry) => entry.documentId),
      );
      setSelection((current) =>
        current.scope === scope
          ? {
              scope,
              ids: new Set([...current.ids].filter((id) => kept.has(id))),
            }
          : current,
      );
    } catch {
      toast.error(
        gt('Could not delete the selected files', {
          context: 'Toast shown when deleting vendor portal files fails',
        }),
      );
    }
  }

  return {
    selectedIds,
    selectedFiles,
    toggle,
    toggleAll,
    clearSelection,
    downloadFile,
    downloadSelected,
    /** Every file the filters match (offered only while they fit in one zip). */
    downloadAll: downloadArchive,
    /** Every file on this page, for downloading more files than one zip takes. */
    downloadPage,
    deleteSelected,
    isDeleting: deleteFiles.isPending,
    /** Zips started from this page, oldest first. */
    startedDownloads,
  };
}

/** Why the server refused a zip, in the user's language. */
function archiveRefusalMessage(
  error: unknown,
  gt: ReturnType<typeof useGT>,
): string {
  if (error instanceof ORPCError && error.code === 'BAD_REQUEST') {
    return gt(
      "Can't download more than {max} files at once. Download them a page at a time, or narrow the filters.",
      {
        context:
          'Toast when a vendor portal zip download is refused because it holds too many files',
        max: MAX_VENDOR_PORTAL_FILES_PER_ARCHIVE,
      },
    );
  }
  if (error instanceof ORPCError && error.code === 'NOT_FOUND') {
    return gt(
      'None of these files are in storage any more, so there is nothing to download.',
      {
        context:
          'Toast when a vendor portal zip download is refused because none of its files are in storage',
      },
    );
  }
  return gt('Could not start the download', {
    context: 'Toast when a zip download of vendor portal files fails to start',
  });
}

/**
 * Save a file from its signed storage link. The link is cross-origin with an inline
 * disposition, so `<a download>` on it is ignored and a PDF or image would replace the page;
 * fetch the bytes and save them through an object URL instead (as DocumentDownloadButton
 * does), opening the link in a new tab only if that fails, and throwing if even that is
 * blocked.
 */
async function saveFile(signedUrl: string, fileName: string) {
  try {
    const response = await fetch(signedUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    const link = window.document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    // The fallback tab opens after awaits, so the browser may no longer count it as the
    // user's click and block it; then say the download failed rather than do nothing.
    if (!window.open(signedUrl, '_blank')) throw error;
  }
}
